import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './styles/App.css';
import API_ENDPOINTS from './config/api';
import { SHOW_LEGACY_TOOLS } from './config/features';

// Components
import Header from './components/Header';
import LandingPage from './components/LandingPage';
import Dashboard from './components/Dashboard';
import ProfileComparison from './components/ProfileComparison';
import UserAudit from './components/UserAudit';
import About from './components/About';
import PaymentWidget from './components/PaymentWidget';
import PaymentSuccess from './components/PaymentSuccess';
import PaymentCancelled from './components/PaymentCancelled';
import ThemeToggle from './components/ThemeToggle';
import DocumentationBrowser from './components/DocumentationBrowser';
import DeploymentReview from './components/DeploymentReview';
import FlowRadarPage from './components/FlowRadarPage';
import PricingPage from './components/PricingPage';

function App() {
  const [isConnected, setIsConnected] = useState(false);
  const [orgInfo, setOrgInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const paymentWidgetRef = useRef();

  useEffect(() => {
    // Check for session token in URL (from OAuth redirect)
    const urlParams = new URLSearchParams(window.location.search);
    const sessionToken = urlParams.get('session');
    
    if (sessionToken) {
      try {
        const sessionData = JSON.parse(atob(sessionToken));
        console.log('🔑 Session token found, setting up connection');
        setIsConnected(true);
        setOrgInfo(sessionData.orgInfo);
        
        // Store session data in localStorage for persistence
        localStorage.setItem('forcesight_session', JSON.stringify(sessionData));
        
        // Clean up URL
        window.history.replaceState({}, document.title, window.location.pathname);
        setLoading(false);
        return;
      } catch (error) {
        console.error('Failed to parse session token:', error);
      }
    }
    
    // Check for stored session in localStorage
    const storedSession = localStorage.getItem('forcesight_session');
    if (storedSession) {
      try {
        const sessionData = JSON.parse(storedSession);
        // Check if session is still valid (less than 24 hours old)
        if (Date.now() - sessionData.timestamp < 24 * 60 * 60 * 1000) {
          console.log('🔑 Using stored session');
          setIsConnected(true);
          setOrgInfo(sessionData.orgInfo);
          setLoading(false);
          return;
        } else {
          localStorage.removeItem('forcesight_session');
        }
      } catch (error) {
        console.error('Failed to parse stored session:', error);
        localStorage.removeItem('forcesight_session');
      }
    }
    
    // Check if user has an active Salesforce session
    checkConnection();
  }, []);

  const checkConnection = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.AUTH_STATUS, {
        method: 'GET',
        credentials: 'include', // Important for cookies
        headers: {
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      console.log('🔍 Connection check result:', data);
      setIsConnected(data.connected);
      setOrgInfo(data.orgInfo || null);
    } catch (error) {
      console.error('Connection check failed:', error);
      setIsConnected(false);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="app-loading">
        <div className="loading-spinner">
          <div className="loading-dot"></div>
          <p className="loading-text">ForceSight</p>
        </div>
      </div>
    );
  }

  const handleDisconnect = async () => {
    localStorage.removeItem('forcesight_session');
    try {
      await fetch(API_ENDPOINTS.LOGOUT, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    setIsConnected(false);
    setOrgInfo(null);
  };

  const showChrome = isConnected;

  return (
    <Router>
      <div className="App">
        {showChrome && (
          <Header
            isConnected={isConnected}
            orgInfo={orgInfo}
            onDisconnect={handleDisconnect}
          />
        )}

        <main className={showChrome ? 'app-main' : undefined}>
          <Routes>
            <Route
              path="/"
              element={
                isConnected ?
                  <Navigate to={SHOW_LEGACY_TOOLS ? '/dashboard' : '/documentation'} replace /> :
                  <LandingPage onConnect={(info) => {
                    setIsConnected(true);
                    setOrgInfo(info);
                  }} />
              }
            />

            <Route
              path="/dashboard"
              element={
                !SHOW_LEGACY_TOOLS ? <Navigate to="/documentation" replace /> :
                isConnected ?
                  <Dashboard orgInfo={orgInfo} paymentWidgetRef={paymentWidgetRef} /> :
                  <Navigate to="/" replace />
              }
            />

            <Route
              path="/compare"
              element={
                !SHOW_LEGACY_TOOLS ? <Navigate to="/documentation" replace /> :
                isConnected ?
                  <ProfileComparison /> :
                  <Navigate to="/" replace />
              }
            />

            <Route
              path="/audit"
              element={
                !SHOW_LEGACY_TOOLS ? <Navigate to="/documentation" replace /> :
                isConnected ?
                  <UserAudit /> :
                  <Navigate to="/" replace />
              }
            />

            <Route
              path="/documentation"
              element={
                isConnected ?
                  <DocumentationBrowser /> :
                  <Navigate to="/" replace />
              }
            />

              <Route
                path="/deployments"
                element={
                  !SHOW_LEGACY_TOOLS ? <Navigate to="/documentation" replace /> :
                  isConnected ?
                    <DeploymentReview /> :
                    <Navigate to="/" replace />
                }
              />

            <Route path="/flow-radar" element={<FlowRadarPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/about" element={<About />} />

            <Route path="/payment-success" element={<PaymentSuccess />} />
            <Route path="/payment-cancelled" element={<PaymentCancelled />} />
          </Routes>
        </main>

        {showChrome && (
          <footer className="app-footer">
            <p>&copy; 2026 ForceSight by Starfish Group</p>
          </footer>
        )}

        <ThemeToggle />

        {showChrome && <PaymentWidget ref={paymentWidgetRef} />}
      </div>
    </Router>
  );
}

export default App;